// src/modules/purityAnalyzer/components/ResultInstrument.jsx
// Sci-fi laboratory HUD — the instrument display shared by Run Test (live
// preview) and the saved Test Result page. Dark console with animated grid,
// scanline sweep, blinking status LEDs, purity gauge, density spectrum,
// calibration-curve plot, metal composition, telemetry tiles, provenance and
// the calculation trace / Archimedes explanation.

import React, { useState } from 'react';
import { Row, Col, Typography, Drawer, Button, Space } from 'antd';
import { QuestionCircleOutlined, ExperimentOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import KaratBadge from './KaratBadge';
import CalculationStepsPanel, { ArchimedesExplanation } from './CalculationStepsPanel';
import StatusLed from './hud/StatusLed';
import DensityScale from './hud/DensityScale';
import CalibrationCurve from './hud/CalibrationCurve';
import MetalComposition from './hud/MetalComposition';
import XrfSpectrum from './hud/XrfSpectrum';
import { purityColor, confidenceColor, FLUID_CONDITION_META } from '../constants/purityConstants';
import './scifi.css';

const { Title } = Typography;

const CYAN = '#00e5ff';

function HudPanel({ title, children, style }) {
  return (
    <div className="pa-panel" style={style}>
      {title && <div className="pa-panel-title">{title}</div>}
      {children}
    </div>
  );
}

function Telemetry({ label, value, suffix, color, delay = 0 }) {
  const display = typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString('en-IN', { maximumFractionDigits: 5 })
    : (value ?? '——');
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
      className="pa-panel"
      style={{ padding: '10px 12px' }}
    >
      <div className="pa-label">{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
        <span className="pa-value" style={{ fontSize: 19, color: color || '#dff6ff' }}>{display}</span>
        {suffix && <span className="pa-label" style={{ letterSpacing: '0.06em' }}>{suffix}</span>}
      </div>
    </motion.div>
  );
}

function ConfidenceMeter({ value = 0 }) {
  const color = confidenceColor(value);
  const segments = 24;
  const lit = Math.round((value / 100) * segments);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span className="pa-label">Confidence</span>
        <span className="pa-value pa-mono" style={{ color, fontSize: 13 }}>{value}%</span>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: segments }).map((_, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: i < lit ? 1 : 0.15 }}
            transition={{ delay: 0.4 + i * 0.03 }}
            style={{
              flex: 1, height: 12, borderRadius: 1,
              background: i < lit ? color : 'rgba(255,255,255,0.18)',
              boxShadow: i < lit ? `0 0 6px ${color}88` : 'none',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function ResultInstrument({ result, context = {}, animateKey, live = false }) {
  const [explainOpen, setExplainOpen] = useState(false);
  if (!result) return null;

  const rawPurity = result.predictedPurity ?? 0;
  const hasDeduction = !!result.deductionPercent && result.deductedPurity != null;
  const purity = hasDeduction ? result.deductedPurity : rawPurity;
  const heroColor = purityColor(purity);
  const fluid = FLUID_CONDITION_META[result.fluidCondition] || FLUID_CONDITION_META.STABLE;
  const fluidColors = { green: '#52e5a3', orange: '#ffb02e', blue: '#69c0ff', red: '#ff4d6d' };
  const points = result.calibrationPoints || [];

  const calibrationNumber =
    result.calibrationSnapshot?.calibrationNumber ||
    result.calibration?.calibrationNumber || '—';
  const versionLabel =
    result.calibrationVersionLabel ||
    result.calibrationVersion?.versionLabel ||
    result.calibrationVersion?.versionNumber || '—';

  return (
    <motion.div
      key={animateKey}
      initial={{ opacity: 0, scale: 0.985 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
    >
      <div className="pa-console" style={{ padding: 18 }}>
        {/* <div className="pa-scanline" /> */}

        {/* ── Status bar ── */}
        <div style={{
          position: 'relative', zIndex: 2, display: 'flex', flexWrap: 'wrap',
          gap: 18, alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
        }}>
          <Space size={18} wrap>
            <StatusLed color="#52e5a3" label="System Online" />
            <StatusLed color={CYAN} label={`Calibration ${calibrationNumber}`} speed="solid" />
            <StatusLed color={fluidColors[fluid.color] || CYAN} label={`Fluid ${fluid.label}`} />
            {live
              ? <StatusLed color="#ffd166" label="Live Analysis" speed="fast" />
              : <StatusLed color="#9be7ff" label={context.testNumber || 'Archived Record'} speed="solid" />}
            {context.status === 'REVERSED' && <StatusLed color="#ff4d6d" label="Reversed" speed="fast" />}
          </Space>
          <span className="pa-mono pa-label" style={{ fontSize: 11 }}>
            AU·ARCHIMEDES·v{result.engineVersion || '1.0.0'} ·{' '}
            {result.processingTimeMs != null ? `${result.processingTimeMs}ms` : '—'}
          </span>
        </div>

        <Row gutter={[16, 16]} style={{ position: 'relative', zIndex: 2 }}>
          {/* ── XRF spectrum (hero) ── */}
          <Col xs={24} lg={16}>
            <HudPanel title="Fluorescence Spectrum · Metal Composition">
              <XrfSpectrum purity={purity} otherMetalPercent={Math.max(0, 100 - purity)} />
            </HudPanel>
          </Col>

          {/* ── Purity readout ── */}
          <Col xs={24} lg={8}>
            <HudPanel title="Purity Readout">
              <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
                <div className="pa-label">Au · Gold</div>
                <div className="pa-value pa-mono pa-flicker" style={{
                  fontSize: 56, lineHeight: 1.15, color: heroColor,
                  textShadow: `0 0 24px ${heroColor}55`,
                }}>
                  {purity.toFixed(2)}<span style={{ fontSize: 26, opacity: 0.8 }}>%</span>
                </div>
                {result.matchLabel && (
                  <div className="pa-label" style={{ marginTop: 4, color: 'rgba(223,246,255,0.8)' }}>
                    {result.matchLabel}
                  </div>
                )}
                {hasDeduction && (
                  <div className="pa-label" style={{ marginTop: 6, opacity: 0.65 }}>
                    Measured {rawPurity.toFixed(2)}% − Deduction {result.deductionPercent}%
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
                  <KaratBadge karat={result.estimatedKarat} fineness={result.karatFineness} size="large" />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <ConfidenceMeter value={result.confidenceScore ?? 0} />
              </div>
            </HudPanel>
          </Col>

          {/* ── Composition table ── */}
          <Col xs={24} md={12}>
            <HudPanel title="Composition Report">
              <MetalComposition purity={purity} otherMetalPercent={Math.max(0, 100 - purity)} />
            </HudPanel>
          </Col>

          {/* ── Calibration curve ── */}
          <Col xs={24} md={12}>
            <HudPanel title={`Calibration Curve · ${versionLabel}`}>
              {points.length >= 2 ? (
                <CalibrationCurve
                  points={points}
                  sample={{ density: result.goldDensity, purity }}
                />
              ) : (
                <div className="pa-label" style={{ padding: 20, textAlign: 'center' }}>
                  Curve unavailable for archived records
                </div>
              )}
              <div className="pa-label" style={{ marginTop: 6, textAlign: 'center' }}>
                <span style={{ color: '#ffd166' }}>—</span> dataset points ·{' '}
                <span style={{ color: CYAN }}>●</span> this sample ({result.interpolationMode || 'INTERPOLATED'})
              </div>
            </HudPanel>
          </Col>

          {/* ── Density spectrum ── */}
          <Col xs={24}>
            <HudPanel title="Density Spectrum · Reference Metals">
              <DensityScale density={result.goldDensity} />
            </HudPanel>
          </Col>

          {/* ── Telemetry tiles ── */}
          <Col xs={12} sm={8} md={4}><Telemetry label="Gold Density" value={result.goldDensity} suffix="g/cm³" color="#ffd166" delay={0.05} /></Col>
          <Col xs={12} sm={8} md={4}><Telemetry label="Diesel Density" value={result.dieselDensity} suffix="g/cm³" color="#b48aff" delay={0.1} /></Col>
          <Col xs={12} sm={8} md={4}><Telemetry label="Immersion Loss" value={result.loss} suffix="g" delay={0.15} /></Col>
          <Col xs={12} sm={8} md={4}><Telemetry label="Buoyancy Ratio" value={result.buoyancyRatio} delay={0.2} /></Col>
          <Col xs={12} sm={8} md={4}><Telemetry label="Temp Index" value={result.temperatureIndex} suffix="%" color={fluidColors[fluid.color]} delay={0.25} /></Col>
          <Col xs={12} sm={8} md={4}><Telemetry label="Other Metals" value={Math.max(0, 100 - purity)} suffix="%" color="#c9d6e2" delay={0.3} /></Col>

          {/* ── Provenance strip ── */}
          <Col xs={24}>
            <HudPanel title="Provenance">
              <Row gutter={[12, 8]}>
                {[
                  ['Machine', context.machine ? `${context.machine.machineCode || ''} ${context.machine.name || ''}`.trim() : '—'],
                  ['Calibration', calibrationNumber],
                  ['Dataset', versionLabel],
                  ['Operator', context.operator?.name || '—'],
                  ['Branch', context.branch?.name || '—'],
                  ['Customer', context.customer?.name || '—'],
                  ['Transaction', context.transactionRef?.label || '—'],
                  ['Timestamp', context.createdAt ? dayjs(context.createdAt).format('DD MMM YYYY · HH:mm:ss') : 'NOT SAVED'],
                ].map(([k, v]) => (
                  <Col xs={12} sm={8} md={6} lg={3} key={k}>
                    <div className="pa-label">{k}</div>
                    <div className="pa-value" style={{ fontSize: 12, marginTop: 2, wordBreak: 'break-word' }}>{v}</div>
                  </Col>
                ))}
              </Row>
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <Button size="small" type="link" icon={<QuestionCircleOutlined />}
                  style={{ color: CYAN }} onClick={() => setExplainOpen(true)}>
                  How was this calculated?
                </Button>
              </div>
            </HudPanel>
          </Col>
        </Row>
      </div>

      {/* ── Calculation steps (standard theme, below the console) ── */}
      <div style={{ marginTop: 12 }}>
        <CalculationStepsPanel steps={result.calculationSteps} />
      </div>

      <Drawer
        title={<span><ExperimentOutlined style={{ marginRight: 8 }} />How Purity Was Calculated</span>}
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        width={520}
      >
        <ArchimedesExplanation />
        <Title level={5}>This sample</Title>
        <CalculationStepsPanel steps={result.calculationSteps} />
      </Drawer>
    </motion.div>
  );
}
